import { Box, Typography } from "@mui/material";
import { progressColor } from "../../helpers";
import { useState } from "react";

/**
 * A circular "sector" progress component built on SVG,
 * to complement MUI's CircularProgress with a pie-slice style.
 */
const clamp = (val, min, max) => Math.min(max, Math.max(min, val));

function describeDonutSegment({
  cx,
  cy,
  outerRadius,
  innerRadius,
  startAngle,
  endAngle,
}) {
  const startRad = (startAngle * Math.PI) / 180;
  const endRad = (endAngle * Math.PI) / 180;

  const x0Outer = cx + outerRadius * Math.cos(startRad);
  const y0Outer = cy + outerRadius * Math.sin(startRad);
  const x1Outer = cx + outerRadius * Math.cos(endRad);
  const y1Outer = cy + outerRadius * Math.sin(endRad);

  const x0Inner = cx + innerRadius * Math.cos(startRad);
  const y0Inner = cy + innerRadius * Math.sin(startRad);
  const x1Inner = cx + innerRadius * Math.cos(endRad);
  const y1Inner = cy + innerRadius * Math.sin(endRad);

  const angleDiff = (endAngle - startAngle + 360) % 360;
  const largeArcFlag = angleDiff > 180 ? 1 : 0;

  return `
    M ${x0Outer} ${y0Outer}
    A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x1Outer} ${y1Outer}
    L ${x1Inner} ${y1Inner}
    A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x0Inner} ${y0Inner}
    Z
  `;
}

export default function SectorCircularProgress({
  values,
  size = 45,
  thickness = 6,
  label,
  fontSize = 12,
  hovered,
  setHovered,
}) {
  const half = size / 2;
  const radius = half - thickness / 2;
  const outerRadius = radius + thickness / 2;
  const innerRadius = radius - thickness / 2;

  const segments = [
    ...values
      .map((r) => ({ value: r.value, color: progressColor(r.total) }))
      .reverse(),
  ];

  let currentAngle = -90; // start at 12 o'clock

  return (
    <Box
      position="relative"
      sx={{ width: size, height: size }}
      pointerEvents="all"
    >
      <svg
        width={size}
        height={size}
        pointerEvents="all"
        style={{ overflow: "visible" }}
      >
        <circle
          cx={half}
          cy={half}
          r={radius}
          fill="none"
          stroke="#e0e0e0"
          strokeWidth={thickness}
        />

        {segments.map((seg, i) => {
          const sweep = (clamp(seg.value, 0, 100) / 100) * 360;
          const endAngle = currentAngle + sweep;

          const d = describeDonutSegment({
            cx: half,
            cy: half,
            outerRadius,
            innerRadius,
            startAngle: currentAngle,
            endAngle: endAngle >= 270 ? endAngle - 0.001 : endAngle, // avoids full-circle arc issue
          });

          currentAngle = endAngle;
          return (
            <path
              key={i}
              d={d}
              fill={seg.color}
              stroke="white"
              strokeWidth={thickness / 20}
              pointerEvents="all"
              onPointerEnter={() => setHovered(i)}
              onPointerLeave={() => setHovered(null)}
              style={{
                cursor: "pointer",
                transform: hovered === i ? "scale(1.05)" : "scale(1)",
                transformOrigin: "center",
                transition: "transform 0.15s",
              }}
            />
          );
        })}
      </svg>

      {/* Label in center */}
      {label !== undefined && (
        <Box
          position="absolute"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ inset: 0, pointerEvents: "none" }}
        >
          {typeof label === "string" || typeof label === "number" ? (
            <Typography
              variant="body2"
              component="div"
              sx={{ fontWeight: "bold", fontSize: fontSize }}
            >
              {label}
            </Typography>
          ) : (
            label
          )}
        </Box>
      )}
    </Box>
  );
}
